/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { MessageCallout, MessageVariety, Spinner } from '@sonarsource/echoes-react';
import { FormattedMessage } from 'react-intl';
import { Path } from 'react-router-dom';
import { isDefined } from '~shared/helpers/types';
import { useJiraOrganizationBindingQuery, useJiraProjectBindingQuery } from '../../queries';
import JiraProjectBindingInfo from './JiraProjectBindingInfo';
import JiraProjectBoundView from './JiraProjectBoundView';
import JiraProjectConnectButton from './JiraProjectConnectButton';
import { JiraProjectWorkTypesForm } from './JiraProjectWorkTypesForm';

interface JiraProjectBindingSettingProps {
  isOrgAdmin: boolean;
  isSdlcDataReleaseOpenJiraTicketsEnabled: boolean;
  orgBindingUrl: Partial<Path>;
  organizationUuid: string;
  shouldCheckForBindingPermissions?: boolean;
  sonarProjectId: string;
  sonarProjectName: string;
}

export function JiraProjectBindingSetting({
  isSdlcDataReleaseOpenJiraTicketsEnabled,
  isOrgAdmin,
  orgBindingUrl,
  organizationUuid,
  sonarProjectId,
  shouldCheckForBindingPermissions,
  sonarProjectName,
}: Readonly<JiraProjectBindingSettingProps>) {
  // Retrieve current organization binding if one exists
  const {
    data: { isOrganizationBound, orgBindingId } = { isOrganizationBound: false },
    error: organizationBindingError,
    isLoading: isLoadingOrganizationBinding,
  } = useJiraOrganizationBindingQuery(organizationUuid, {
    select: (data) => {
      return {
        isOrganizationBound: !!data && !!data.jiraInstanceUrl,
        orgBindingId: data?.id,
      };
    },
  });

  const {
    data: projectBinding,
    error: projectBindingError,
    isLoading: isLoadingProjectBinding,
  } = useJiraProjectBindingQuery(sonarProjectId, { enabled: isOrganizationBound });

  const isServerError = isDefined(organizationBindingError) || isDefined(projectBindingError);
  const isProjectBound = Boolean(
    projectBinding?.jiraProjectKey && projectBinding.jiraOrganizationId,
  );

  const isLoading = isLoadingOrganizationBinding || isLoadingProjectBinding;

  return (
    <div className="sw-px-6">
      <Spinner isLoading={isLoading}>
        <JiraProjectBindingInfo isProjectBound={isProjectBound} />
        {projectBinding && isProjectBound && isOrganizationBound && (
          <JiraProjectBoundView
            orgBindingId={orgBindingId}
            orgId={organizationUuid}
            projectBinding={projectBinding}
            shouldCheckForBindingPermissions={shouldCheckForBindingPermissions}
            sonarProjectName={sonarProjectName}
          />
        )}
        {!isProjectBound && (
          <JiraProjectConnectButton
            hasError={isServerError}
            isOrgAdmin={isOrgAdmin}
            isOrganizationBound={isOrganizationBound}
            orgBindingId={orgBindingId}
            orgBindingUrl={orgBindingUrl}
            orgId={organizationUuid}
            shouldCheckForBindingPermissions={shouldCheckForBindingPermissions}
            sonarProjectId={sonarProjectId}
            sonarProjectName={sonarProjectName}
          />
        )}

        {isServerError && (
          <MessageCallout variety={MessageVariety.Danger}>
            <FormattedMessage id="jira_binding.server_error" />
          </MessageCallout>
        )}

        {isSdlcDataReleaseOpenJiraTicketsEnabled && isProjectBound && !isServerError && (
          <JiraProjectWorkTypesForm
            jiraProjectKey={projectBinding?.jiraProjectKey ?? ''}
            sonarOrganizationUuid={organizationUuid}
            sonarProjectId={sonarProjectId}
          />
        )}
      </Spinner>
    </div>
  );
}
